import { http } from "./http";
import type {
  CreateTestReferralRewardPayload,
  Coupon,
  CouponRedemption,
  MarketingEmailCaptureFilters,
  MarketingEmailCaptureListResponse,
  ReferralRelationship,
  ReferralReward,
  ReferralSettings,
  SendMarketingEmailCapturePayload,
  SendMarketingEmailCaptureResult,
  TestReferralRewardResult,
} from "../types/promotions";

type Paged<T> = {
  items: T[];
  page: number;
  limit: number;
  total: number;
  pages: number;
};

const BASE = "/admin/promotions";

export const PromotionsAdminAPI = {
  async getReferralSettings() {
    const res = await http.get<{ settings: ReferralSettings }>(`${BASE}/settings`);
    return res.data.settings;
  },

  async updateReferralSettings(payload: Partial<ReferralSettings>) {
    const res = await http.put<{ settings: ReferralSettings }>(`${BASE}/settings`, payload);
    return res.data.settings;
  },

  async listCoupons(params?: Record<string, unknown>) {
    const res = await http.get<Paged<Coupon>>(`${BASE}/coupons`, { params });
    return res.data;
  },

  async createCoupon(payload: Partial<Coupon> & { code: string; discountType: string; discountValue: number }) {
    const res = await http.post<{ coupon: Coupon }>(`${BASE}/coupons`, payload);
    return res.data.coupon;
  },

  async updateCoupon(id: string, payload: Partial<Coupon>) {
    const res = await http.patch<{ coupon: Coupon }>(
      `${BASE}/coupons/${encodeURIComponent(id)}`,
      payload,
    );
    return res.data.coupon;
  },

  async deleteCoupon(id: string) {
    const res = await http.delete<{ message: string }>(
      `${BASE}/coupons/${encodeURIComponent(id)}`,
    );
    return res.data;
  },

  async listReferrals(params?: Record<string, unknown>) {
    const res = await http.get<Paged<ReferralRelationship>>(`${BASE}/referrals`, {
      params,
    });
    return res.data;
  },

  async listRewards(params?: Record<string, unknown>) {
    const res = await http.get<Paged<ReferralReward>>(`${BASE}/rewards`, { params });
    return res.data;
  },

  async createTestReferralRewardToken(payload: CreateTestReferralRewardPayload) {
    const res = await http.post<{ reward: TestReferralRewardResult }>(
      `${BASE}/rewards/test-token`,
      payload,
    );
    return res.data.reward;
  },

  async listCouponRedemptions(params?: Record<string, unknown>) {
    const res = await http.get<Paged<CouponRedemption>>(`${BASE}/coupon-redemptions`, {
      params,
    });
    return res.data;
  },

  async listEmailCaptures(params?: MarketingEmailCaptureFilters & { page?: number; limit?: number }) {
    const res = await http.get<MarketingEmailCaptureListResponse>(
      `${BASE}/email-captures`,
      { params },
    );
    return res.data;
  },

  async sendEmailCaptures(payload: SendMarketingEmailCapturePayload) {
    const res = await http.post<SendMarketingEmailCaptureResult>(
      `${BASE}/email-captures/send`,
      payload,
      { timeout: 120000 },
    );
    return res.data;
  },
};
